<template>
  <div
    class="em-tabs"
    :class="[`tabs-variant-${variant}`, `tabs-size-${size}`, { 'tabs-centered': centered }]"
  >
    <div class="em-tabs-header" role="tablist">
      <button
        v-for="(tab, index) in tabs"
        :key="tab.value"
        class="em-tab-button"
        :class="{
          active: modelValue === tab.value,
          disabled: tab.disabled,
        }"
        role="tab"
        :aria-selected="modelValue === tab.value"
        :aria-disabled="tab.disabled"
        :tabindex="modelValue === tab.value ? 0 : -1"
        @click="!tab.disabled && selectTab(tab.value)"
        @keydown.left="navigateTabs(index, -1)"
        @keydown.right="navigateTabs(index, 1)"
      >
        <span v-if="tab.icon" class="tab-icon">
          <IconRenderer :name="tab.icon" size="small" />
        </span>
        <span class="tab-label">{{ tab.label }}</span>
        <span v-if="tab.badge" class="tab-badge">{{ tab.badge }}</span>
      </button>
    </div>
    <div class="em-tabs-content" role="tabpanel">
      <slot :activeTab="modelValue" />
    </div>
  </div>
</template>

<script setup lang="ts">
import IconRenderer from '@/common/components/ui/icon/IconRenderer.vue';

export interface Tab {
  label: string;
  value: string;
  icon?: string;
  badge?: string | number;
  disabled?: boolean;
}

interface Props {
  tabs: Tab[];
  modelValue: string;
  variant?: 'default' | 'pills' | 'underline';
  size?: 'small' | 'medium' | 'large';
  centered?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'default',
  size: 'medium',
  centered: false,
});

const emit = defineEmits<{
  'update:modelValue': [value: string];
  change: [value: string];
}>();

const selectTab = (value: string) => {
  emit('update:modelValue', value);
  emit('change', value);
};

const navigateTabs = (currentIndex: number, direction: number) => {
  const enabledTabs = props.tabs.filter((tab) => !tab.disabled);
  const currentTab = props.tabs[currentIndex];
  const currentEnabledIndex = enabledTabs.findIndex((t) => t.value === currentTab.value);

  let nextIndex = currentEnabledIndex + direction;
  if (nextIndex < 0) nextIndex = enabledTabs.length - 1;
  if (nextIndex >= enabledTabs.length) nextIndex = 0;

  selectTab(enabledTabs[nextIndex].value);
};
</script>

<style scoped>
.em-tabs {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.em-tabs.tabs-centered {
  align-items: center;
}

.em-tabs-header {
  display: inline-flex;
  gap: var(--ion-space-2, 8px);
  padding: 4px;
  background: var(--ion-background-color);
  border: 2px solid var(--ion-input-border-color);

  border-radius: var(--ion-radius-xl, 16px);
  width: fit-content;
}

.em-tab-button {
  display: flex;
  align-items: center;
  gap: var(--ion-space-2, 8px);
  padding: 10px 20px;
  border: none;
  background: transparent;
  border-radius: var(--ion-radius-lg, 12px);
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  color: var(--ion-text-color-secondary, #666);
  transition: all 0.2s ease;
  white-space: nowrap;
}

.em-tab-button:hover:not(.disabled) {
  background: var(--ion-background-color-tertiary);
  color: var(--ion-text-color, #333);
}

.em-tab-button.active {
  background: var(--ion-color-primary);
  color: var(--ion-text-color-contrast);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.em-tab-button.disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tab-icon {
  display: flex;
  align-items: center;
}

.tab-label {
  line-height: 1;
}

.tab-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 20px;
  height: 20px;
  padding: 0 6px;
  background: var(--ion-color-primary, #3880ff);
  color: #fff;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
}

.em-tabs-content {
  padding-top: var(--ion-space-4, 16px);
}

/* Variants */
.em-tabs.tabs-variant-underline .em-tabs-header {
  background: transparent;
  padding: 0;
  border-bottom: 1px solid var(--ion-border-color, #e0e0e0);
  border-radius: 0;
  gap: 0;
}

.em-tabs.tabs-variant-underline .em-tab-button {
  border-radius: 0;
  padding: 12px 24px;
  position: relative;
}

.em-tabs.tabs-variant-underline .em-tab-button.active {
  background: transparent;
  box-shadow: none;
}

.em-tabs.tabs-variant-underline .em-tab-button.active::after {
  content: '';
  position: absolute;
  bottom: -1px;
  left: 0;
  right: 0;
  height: 2px;
  background: var(--ion-color-primary, #3880ff);
}

.em-tabs.tabs-variant-pills .em-tabs-header {
  background: transparent;
  padding: 0;
}

.em-tabs.tabs-variant-pills .em-tab-button {
  border: 1px solid var(--ion-border-color, #e0e0e0);
}

.em-tabs.tabs-variant-pills .em-tab-button.active {
  background: var(--ion-color-primary, #3880ff);
  color: #fff;
  border-color: var(--ion-color-primary, #3880ff);
}

/* Sizes */
.em-tabs.tabs-size-small .em-tab-button {
  padding: 6px 14px;
  font-size: 12px;
}

.em-tabs.tabs-size-large .em-tab-button {
  padding: 14px 28px;
  font-size: 16px;
}
</style>
